import type { StagedValue } from "@/types/contract-family-pipeline";

import type { PipelineSummaryGridCellDetail, PipelineSummaryGridResponse } from "./pipelinePreview.types";
import type { SnapshotGridRowModel } from "./rowModel";
import type { SnapshotGridField } from "@/modules/snapshots/businessReports.api";

export const patchPipelineStagedValue = (
  page: PipelineSummaryGridResponse,
  stagedOutcomeId: string,
  fieldId: string,
  value: StagedValue,
): PipelineSummaryGridResponse => {
  const effectiveValue = value.override_value ?? value.generated_value;
  let patched = false;
  const groups = page.groups.map(group => {
    let groupPatched = false;
    const rows = group.rows.map(row => {
      if (row.staged_outcome_id !== stagedOutcomeId) return row;
      let rowPatched = false;
      const cells = row.cells.map(cell => {
        if (cell.field_id !== fieldId) return cell;
        patched = true;
        groupPatched = true;
        rowPatched = true;
        const field = page.fields.find(
          candidate => candidate.business_report_prompt_id === fieldId || candidate.key === cell.field_key,
        );
        return {
          ...cell,
          value: effectiveValue,
          date: field?.kind === "date" ? normalizePipelineDate(effectiveValue) : null,
          rating: field?.kind === "rating" ? normalizePipelineRating(effectiveValue) : null,
          overridden: value.override_value !== null,
        };
      });
      return rowPatched ? { ...row, cells } : row;
    });
    return groupPatched ? { ...group, rows } : group;
  });

  return patched ? { ...page, groups } : page;
};

export const patchPipelineSummaryGridCellDetail = (
  detail: PipelineSummaryGridCellDetail,
  fieldId: string,
  value: StagedValue,
): PipelineSummaryGridCellDetail => {
  if (detail.field_id !== fieldId) return detail;
  return {
    ...detail,
    effective_value: value.override_value ?? value.generated_value,
    generated_value: value.generated_value,
    override_value: value.override_value,
  };
};

export const patchPipelineSummaryGridRows = (
  rows: SnapshotGridRowModel[],
  fields: SnapshotGridField[],
  stagedOutcomeId: string,
  fieldId: string,
  value: StagedValue,
): SnapshotGridRowModel[] => {
  const effectiveValue = value.override_value ?? value.generated_value;
  return rows.map(row => {
    if (row.presentation.stagedOutcomeId !== stagedOutcomeId) return row;
    let changed = false;
    const cells = row.cells.map(cell => {
      if (cell.fieldId !== fieldId) return cell;
      changed = true;
      const field = fields.find(
        candidate => candidate.business_report_prompt_id === fieldId || candidate.key === cell.fieldKey,
      );
      return {
        ...cell,
        value: effectiveValue,
        date: field?.kind === "date" ? normalizePipelineDate(effectiveValue) : null,
        rating: field?.kind === "rating" ? normalizePipelineRating(effectiveValue) : null,
        overridden: value.override_value !== null,
      };
    });
    return changed ? { ...row, cells } : row;
  });
};

const normalizePipelineDate = (value: string): string | null => {
  const calendarDate = parsePipelineCalendarDate(value);
  if (calendarDate) return calendarDate;

  const dateTime = /^((?:\d{4}-\d{2}-\d{2})|(?:\d{8})|(?:\d{4}-W\d{2}(?:-\d)?)|(?:\d{4}W\d{2}\d?))(.)((?:.+))$/su.exec(
    value,
  );
  if (!dateTime) return null;
  const date = parsePipelineCalendarDate(dateTime[1]);
  const time = parsePipelineTime(dateTime[3]);
  if (!date || !time) return null;
  if (time.hour === 24) return incrementPipelineDate(date);
  return date;
};

const parsePipelineCalendarDate = (value: string): string | null => {
  const calendarMatch = /^(?:(\d{4})-(\d{2})-(\d{2})|(\d{4})(\d{2})(\d{2}))$/.exec(value);
  if (calendarMatch)
    return formatPipelineDate(
      Number(calendarMatch[1] ?? calendarMatch[4]),
      Number(calendarMatch[2] ?? calendarMatch[5]),
      Number(calendarMatch[3] ?? calendarMatch[6]),
    );

  const weekMatch = /^(\d{4})-W(\d{2})(?:-(\d))?$/.exec(value) ?? /^(\d{4})W(\d{2})(\d)?$/.exec(value);
  if (!weekMatch) return null;
  const year = Number(weekMatch[1]);
  const week = Number(weekMatch[2]);
  const weekday = weekMatch[3] === undefined ? 1 : Number(weekMatch[3]);
  if (year < 1 || week < 1 || week > isoWeeksInYear(year) || weekday < 1 || weekday > 7) return null;

  const weekOneMonday = dateOrdinal(year, 1, 4) - (isoWeekday(dateOrdinal(year, 1, 4)) - 1);
  return dateFromOrdinal(weekOneMonday + (week - 1) * 7 + weekday - 1);
};

const formatPipelineDate = (year: number, month: number, day: number): string | null => {
  if (year < 1 || year > 9999 || month < 1 || month > 12 || day < 1 || day > daysInMonth(year, month)) return null;
  return `${String(year).padStart(4, "0")}-${String(month).padStart(2, "0")}-${String(day).padStart(2, "0")}`;
};

const parsePipelineTime = (value: string): { hour: number } | null => {
  const extended = /^(\d{2}):(\d{2})(?::(\d{2})([.,]\d+)?)?(Z|[+-].+)?$/.exec(value);
  if (extended) return buildPipelineTime(extended[1], extended[2], extended[3], extended[4], extended[5]);

  const basicWithSeconds = /^(\d{2})(\d{2})(\d{2})([.,]\d+)?(Z|[+-].+)?$/.exec(value);
  if (basicWithSeconds)
    return buildPipelineTime(
      basicWithSeconds[1],
      basicWithSeconds[2],
      basicWithSeconds[3],
      basicWithSeconds[4],
      basicWithSeconds[5],
    );

  const basicWithMinutes = /^(\d{2})(\d{2})(Z|[+-].+)?$/.exec(value);
  if (basicWithMinutes)
    return buildPipelineTime(basicWithMinutes[1], basicWithMinutes[2], undefined, undefined, basicWithMinutes[3]);

  const hourOnly = /^(\d{2})(Z|[+-].+)?$/.exec(value);
  if (hourOnly) return buildPipelineTime(hourOnly[1], undefined, undefined, undefined, hourOnly[2]);
  return null;
};

const buildPipelineTime = (
  hourValue: string,
  minuteValue: string | undefined,
  secondValue: string | undefined,
  fraction: string | undefined,
  offset: string | undefined,
): { hour: number } | null => {
  const hour = Number(hourValue);
  const minute = minuteValue === undefined ? 0 : Number(minuteValue);
  const second = secondValue === undefined ? 0 : Number(secondValue);
  if (minute > 59 || second > 59 || hour > 24 || !isPipelineOffset(offset)) return null;
  if (hour === 24 && (minute !== 0 || second !== 0 || (fraction !== undefined && !/^([.,]0+)$/.test(fraction))))
    return null;
  return { hour };
};

const isPipelineOffset = (value: string | undefined): boolean => {
  if (value === undefined || value === "Z") return true;
  if (/^[+-]\d{4}[.,]/.test(value)) return false;
  const offset =
    /^([+-])(\d{2}):(\d{2})(?::(\d{2})([.,]\d+)?)?$/.exec(value) ??
    /^([+-])(\d{2})(\d{2})(\d{2})?([.,]\d+)?$/.exec(value) ??
    /^([+-])(\d{2})$/.exec(value);
  if (!offset) return false;
  const hours = Number(offset[2]);
  const minutes = offset[3] === undefined ? 0 : Number(offset[3]);
  const seconds = offset[4] === undefined ? 0 : Number(offset[4]);
  return hours < 24 && minutes < 60 && seconds < 60;
};

const incrementPipelineDate = (value: string): string | null => {
  const [year, month, day] = value.split("-").map(Number);
  return dateFromOrdinal(dateOrdinal(year, month, day) + 1);
};

const isLeapYear = (year: number): boolean => year % 4 === 0 && (year % 100 !== 0 || year % 400 === 0);

const daysInMonth = (year: number, month: number): number => {
  const days = [31, isLeapYear(year) ? 29 : 28, 31, 30, 31, 30, 31, 31, 30, 31, 30, 31];
  return days[month - 1];
};

const dateOrdinal = (year: number, month: number, day: number): number => {
  let ordinal = day;
  for (let currentMonth = 1; currentMonth < month; currentMonth += 1) ordinal += daysInMonth(year, currentMonth);
  const previousYear = year - 1;
  return (
    ordinal +
    previousYear * 365 +
    Math.floor(previousYear / 4) -
    Math.floor(previousYear / 100) +
    Math.floor(previousYear / 400)
  );
};

const dateFromOrdinal = (ordinal: number): string | null => {
  if (ordinal < 1 || ordinal > dateOrdinal(9999, 12, 31)) return null;
  let lowerYear = 1;
  let upperYear = 9999;
  while (lowerYear < upperYear) {
    const middleYear = Math.floor((lowerYear + upperYear + 1) / 2);
    if (dateOrdinal(middleYear, 1, 1) <= ordinal) lowerYear = middleYear;
    else upperYear = middleYear - 1;
  }
  let remainingDays = ordinal - dateOrdinal(lowerYear, 1, 1) + 1;
  let month = 1;
  while (remainingDays > daysInMonth(lowerYear, month)) {
    remainingDays -= daysInMonth(lowerYear, month);
    month += 1;
  }
  return formatPipelineDate(lowerYear, month, remainingDays);
};

const isoWeekday = (ordinal: number): number => ((ordinal - 1) % 7) + 1;

const isoWeeksInYear = (year: number): number => {
  const decemberTwentyEighth = dateOrdinal(year, 12, 28);
  const weekOneMonday = dateOrdinal(year, 1, 4) - (isoWeekday(dateOrdinal(year, 1, 4)) - 1);
  return Math.floor((decemberTwentyEighth - weekOneMonday) / 7) + 1;
};

const normalizePipelineRating = (value: string): string | null => {
  const rating = value.toUpperCase();
  return rating === "RED" || rating === "AMBER" || rating === "GREEN" ? rating : null;
};
